// Mercado Pago (etapa 9): cobro de la suscripción con Checkout Pro. Funciones puras compartidas
// por la app y las Edge Functions (WebCrypto, sin dependencias).

/** Meses que se pueden pagar de una vez. */
export const MP_MONTH_OPTIONS = [1, 3, 6, 12] as const;
export type MpMonths = (typeof MP_MONTH_OPTIONS)[number];

export const isMpMonths = (value: unknown): value is MpMonths => MP_MONTH_OPTIONS.includes(value as MpMonths);

/** Mercado Pago trabaja en pesos con decimales; la base, en centavos. */
export const centsToMpAmount = (cents: number): number => Math.round(cents) / 100;
export const mpAmountToCents = (amount: number): number => Math.round(amount * 100);

/** "ts=1704908010,v1=618c8534…" → { ts, v1 }. */
export function parseMpSignature(header: string | null | undefined): { ts: string; v1: string } | null {
  if (!header) return null;
  const parts = Object.fromEntries(
    header.split(',').map((p) => {
      const [k, ...v] = p.split('=');
      return [k?.trim() ?? '', v.join('=').trim()];
    }),
  );
  return parts.ts && parts.v1 ? { ts: parts.ts, v1: parts.v1 } : null;
}

/**
 * Texto que Mercado Pago firma: "id:[data.id];request-id:[x-request-id];ts:[ts];". Si falta
 * algún dato, esa parte no va. El id alfanumérico va en minúsculas.
 */
export function mpSignatureManifest(dataId: string | null, requestId: string | null, ts: string): string {
  let manifest = '';
  if (dataId) manifest += `id:${/^[a-z0-9]+$/i.test(dataId) ? dataId.toLowerCase() : dataId};`;
  if (requestId) manifest += `request-id:${requestId};`;
  return `${manifest}ts:${ts};`;
}

async function hmacHex(secret: string, message: string): Promise<string> {
  const enc = new TextEncoder();
  const key = await crypto.subtle.importKey('raw', enc.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
  const sig = new Uint8Array(await crypto.subtle.sign('HMAC', key, enc.encode(message)));
  return Array.from(sig, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** Comparación sin cortar en el primer carácter distinto. */
function sameText(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}

/** Firma del aviso (webhook) de Mercado Pago, con la clave secreta de la aplicación. */
export async function verifyMpSignature(input: {
  header: string | null | undefined;
  requestId: string | null;
  dataId: string | null;
  secret: string;
}): Promise<boolean> {
  const sig = parseMpSignature(input.header);
  if (!sig || !input.secret) return false;
  const expected = await hmacHex(input.secret, mpSignatureManifest(input.dataId, input.requestId, sig.ts));
  return sameText(expected, sig.v1.toLowerCase());
}

/** Firma de prueba (para los tests). */
export async function signMpManifest(secret: string, dataId: string | null, requestId: string | null, ts: string): Promise<string> {
  return `ts=${ts},v1=${await hmacHex(secret, mpSignatureManifest(dataId, requestId, ts))}`;
}
